import React, { useEffect, useState } from "react";
import { auth, functions } from "../firebase/config";
import { verifyPasswordResetCode, confirmPasswordReset, signInWithEmailAndPassword } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useSearchParams, useNavigate } from "react-router-dom";
import { notificationService } from "../services/notificationService";
import "../styles/auth.css";

const ResetPassword: React.FC = () => {
  const [oobCode, setOobCode] = useState("");
  const [validCode, setValidCode] = useState(false);
  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const code = searchParams.get("oobCode") || "";
    setOobCode(code);

    if (!code) {
      setError("No reset code provided.");
      return;
    }

    verifyPasswordResetCode(auth, code)
      .then((verifiedEmail) => {
        setEmail(verifiedEmail);
        setValidCode(true);
      })
      .catch((err: any) => {
        switch (err.code) {
          case "auth/expired-action-code":
            setError("This password reset link has expired.");
            break;
          case "auth/invalid-action-code":
            setError("Invalid or malformed reset link.");
            break;
          default:
            setError("The reset link is invalid or expired.");
        }
      });
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);

      try {
        await signInWithEmailAndPassword(auth, email, password);
        await httpsCallable(functions, "revokeOtherSessions")({
          deviceId: notificationService.getCurrentDeviceId(),
        });
      } catch (revokeErr) {
        console.error("[ResetPassword] Failed to revoke other sessions:", revokeErr);
      }

      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      switch (err.code) {
        case "auth/expired-action-code":
          setError("This reset link has expired. Request a new one.");
          break;
        case "auth/invalid-action-code":
          setError("The reset link is invalid. Try requesting a new one.");
          break;
        case "auth/user-disabled":
          setError("This account has been disabled.");
          break;
        case "auth/weak-password":
          setError("Password is too weak. Please choose a stronger one.");
          break;
        case "auth/network-request-failed":
          setError("Network error. Check your connection and try again.");
          break;
        default:
          setError("Password reset failed. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // Success Screen
  if (done) {
    return (
      <div className="w-full bg-base-100">
        <div className="flex items-center justify-center max-w-xl mx-auto px-4 min-h-screen bg-base-100">
          <div className="p-8 w-full mx-auto text-center">
            <h1 className="text-2xl font-semibold text-base-content mb-4">
              Password Reset Successful
            </h1>
            <p className="text-base-content/70 mb-6">
              You will be redirected to login shortly.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="text-primary hover:text-primary-dark font-medium"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Invalid link UI
  if (!validCode) {
    return (
      <div className="w-full bg-base-100">
        <div className="flex items-center justify-center max-w-xl mx-auto px-4 min-h-screen">
          <div className="p-8 w-full mx-auto text-center">
            <p className="text-red-500">{error || "Checking reset link..."}</p>
          </div>
        </div>
      </div>
    );
  }

  // Main Reset Form
  return (
    <div className="w-full bg-base-100">
      <div className="flex items-center justify-center max-w-xl mx-auto px-4 min-h-screen">
        <div className="p-8 w-full mx-auto">
          <h1 className="text-2xl font-semibold text-center text-base-content mb-4">
            Reset Your Password
          </h1>

          {error && (
            <div className="alert alert-error mb-4 text-center">
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 w-full">
            <div className="form-group w-full">
              <div className="new-form-group w-full">
                <input
                  className="form-element"
                  type="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <label>New Password</label>
              </div>
              <p className="text-xs text-base-content/60 mt-1">
                Minimum 6 characters
              </p>
            </div>

            <div className="form-group w-full">
              <div className="new-form-group w-full">
                <input
                  className="form-element"
                  type="password"
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <label>Confirm Password</label>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={saving}
            >
              {saving ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-base-100 mr-2"></div>
                  Updating…
                </div>
              ) : (
                "Reset Password"
              )}
            </button>

            <div className="text-center pt-4">
              <p className="text-sm text-base-content/70">
                Remember your password?{" "}
                <button
                  onClick={() => navigate("/login")}
                  className="text-base-content hover:text-base-content/80 font-medium"
                >
                  Back to Sign In
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
